import { useI18n } from "vue-i18n";
import { useUser } from "~/composables/useUser";
import { useProModal } from "@/store/useProModal";
import type { UpsellGate } from "~/composables/useUpsellTracking";

/**
 * State-aware Pro gate copy and call to action:
 * - guest (not signed in)        -> sign in
 * - free user who can still trial -> start the free trial
 * - free user past the trial      -> upgrade to Pro
 */
export const useProGateHint = () => {
  const { user } = useUser();
  const { t } = useI18n();
  const proModal = useProModal();
  const { requireLogin } = useAuthGate();
  const { trackUpsell } = useUpsellTracking();

  const gateState = computed<UpsellGate>(() => {
    if (!user.value) return "guest";
    return user.value?.canUseTrial !== false ? "trial" : "upgrade";
  });

  const proGateHint = computed(() => {
    if (gateState.value === "guest") return t("authModal.proLoginPrompt");
    return gateState.value === "trial"
      ? t("announcements.startTrialToUse")
      : t("announcements.upgradeProToUse");
  });

  const ctaLabel = computed(() => {
    if (gateState.value === "guest") return t("authModal.loginButton");
    return gateState.value === "trial"
      ? t("buttons.startFreeTrial")
      : t("buttons.upgrade");
  });

  /** Opens the sign-in modal for guests, the plan picker otherwise. */
  const openProGate = (feature: string) => {
    trackUpsell("gate_click", { feature, gate: gateState.value });
    if (gateState.value === "guest") {
      requireLogin();
      return;
    }
    proModal.onOpen("subscription", { feature });
  };

  return { proGateHint, gateState, ctaLabel, openProGate };
};
